// Frontend Messages Constants
// Use these for client-side validation, UI feedback, and user-facing messages
// that don't come from backend API responses

const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const FRONTEND_MESSAGES = {
    // Form Validation
    VALIDATION: {
        REQUIRED_FIELD: (field) => `Please enter ${field.toLowerCase()}.`,
        INVALID_EMAIL: "Please enter the email address in a valid format.",
        INVALID_PHONE: "Please enter the phone number in a valid format.",
        INVALID_URL: "Please enter a valid URL.",
        INVALID_FORMAT: (field) => `Invalid ${field.toLowerCase()} format.`,
        MIN_LENGTH: (field, length) => `${capitalize(field)} must be at least ${length} characters.`,
        MAX_LENGTH: (field, length) => `${capitalize(field)} must not exceed ${length} characters.`,
        MIN_VALUE: (field, value) => `${capitalize(field)} must be at least ${value}.`,
        MAX_VALUE: (field, value) => `${capitalize(field)} must not exceed ${value}.`,
        PASSWORD_MISMATCH: "Password and confirm password do not match.",
        PASSWORD_WEAK: "Password must contain at least 8 characters, including uppercase, lowercase, number and special character.",
        INVALID_DATE: "Please enter a valid date.",
        FUTURE_DATE_REQUIRED: "Date must be in the future.",
        PAST_DATE_REQUIRED: "Date must be in the past.",
        INVALID_FILE_TYPE: (types) => `Please upload a valid file type: ${types}.`,
        FILE_TOO_LARGE: (size) => `File size must not exceed ${size}.`,
        SELECT_OPTION: (field) => `Please select ${field.toLowerCase()}.`,
        ACCEPT_TERMS: "Please accept the terms and conditions.",
        NUMERIC_ONLY: (field) => `${capitalize(field)} must contain only numbers.`,
        ALPHA_ONLY: (field) => `${capitalize(field)} must contain only letters.`,
        ALPHANUMERIC_ONLY: (field) => `${capitalize(field)} must contain only letters and numbers.`,
        POSITIVE_ONLY: (field) => `${capitalize(field)} must be greater than 0.`,
        POSITIVE_DECIMAL: (field) => `${capitalize(field)} must be a valid positive number.`,
        NO_DECIMAL: (field) => `${capitalize(field)} must be a whole number.`,
        RANGE_VALID: (field, min, max) => `${capitalize(field)} must be between ${min} and ${max}.`,
        ALPHA_SPECIAL_ONLY: (field) => `${capitalize(field)} must contain only letters and allowed special characters.`,
        NO_NUMBERS_ALLOWED: (field) => `${capitalize(field)} cannot contain numbers.`,
        INVALID_CHARACTERS: (field) => `${capitalize(field)} contains invalid characters.`,
        FILE_TYPE_INVALID: (types) => `Only ${types.map(t => `.${t}`).join(", ")} formats are supported.`,
        FILE_SIZE_MAX: (size, fileType = "File") => `${fileType} size must not exceed ${size}.`,
        PASSWORD_UPPERCASE: "Password must contain at least one uppercase letter.",
        PASSWORD_LOWERCASE: "Password must contain at least one lowercase letter.",
        PASSWORD_NUMBER: "Password must contain at least one number.",
        PASSWORD_SPECIAL: "Password must contain at least one special character.",
        NO_SPACES: (field) => `${capitalize(field)} must not contain spaces.`,
        AT_LEAST_ONE_REQUIRED: (fields) => `At least one of the following is required: ${fields.join(", ")}.`,
    },

    PASSWORD_RULES: {
        RULES: {
            LENGTH: "Between 8-20 characters.",
            UPPERCASE: "One uppercase letter (A-Z).",
            LOWERCASE: "One lowercase letter (a-z).",
            NUMBER: "One number (0-9).",
            SPECIAL_CHAR: "One special character (!@#$...).",
            NO_SPACES: "No spaces allowed.",
        },
        STRENGTH: {
            VERY_WEAK: "Very Weak",
            WEAK: "Weak",
            FAIR: "Fair",
            STRONG: "Strong",
            VERY_STRONG: "Very Strong",
        },
    },
    // UI Feedback
    UI: {
        LOADING: "Loading...",
        PROCESSING: "Processing...",
        SAVING: "Saving...",
        DELETING: "Deleting...",
        UPLOADING: "Uploading...",
        DOWNLOADING: "Downloading...",
        PLEASE_WAIT: "Please wait...",
        NO_DATA: "No data available.",
        NO_RESULTS: "No results found.",
        SEARCH_PLACEHOLDER: "Search...",
        FILTER_PLACEHOLDER: "Filter by...",
        SELECT_PLACEHOLDER: "Select an option",
        EMPTY_STATE: (module) => `No ${module.toLowerCase()} found.`,
        CONFIRM_DELETE: (item) => `Are you sure you want to delete this ${item.toLowerCase()}?`,
        CONFIRM_ACTION: (action) => `Are you sure you want to ${action.toLowerCase()}?`,
        UNSAVED_CHANGES: "You have unsaved changes. Are you sure you want to leave?",
        COPY_SUCCESS: "Copied to clipboard.",
        COPY_FAILED: "Failed to copy to clipboard.",
    },

    // Network & Connection
    NETWORK: {
        OFFLINE: "You are currently offline. Please check your internet connection.",
        RECONNECTING: "Reconnecting...",
        CONNECTION_LOST: "Connection lost. Trying to reconnect...",
        CONNECTION_RESTORED: "Connection restored.",
        SLOW_CONNECTION: "Slow internet connection detected.",
        REQUEST_TIMEOUT: "Request timed out. Please try again.",
        REQUEST_FAILED: "Request failed. Please try again.",
    },

    // File Operations
    FILE: {
        UPLOAD_SUCCESS: "File uploaded successfully.",
        UPLOAD_FAILED: "Failed to upload file.",
        UPLOAD_PROGRESS: (percent) => `Uploading... ${percent}%`,
        DOWNLOAD_SUCCESS: "File downloaded successfully.",
        DOWNLOAD_FAILED: "Failed to download file.",
        DELETE_SUCCESS: "File deleted successfully.",
        DELETE_FAILED: "Failed to delete file.",
        INVALID_FILE: "Invalid file selected.",
        NO_FILE_SELECTED: "No file selected.",
        DRAG_DROP_HERE: "Drag and drop files here or click to browse.",
        MAX_FILES_EXCEEDED: (max) => `You can upload maximum ${max} files.`,
    },

    // Form Actions
    FORM: {
        SAVE_SUCCESS: "Changes saved successfully.",
        SAVE_FAILED: "Failed to save changes.",
        UPDATE_SUCCESS: "Updated successfully.",
        UPDATE_FAILED: "Failed to update.",
        CREATE_SUCCESS: "Created successfully.",
        CREATE_FAILED: "Failed to create.",
        DELETE_SUCCESS: "Deleted successfully.",
        DELETE_FAILED: "Failed to delete.",
        SUBMIT_SUCCESS: "Submitted successfully.",
        SUBMIT_FAILED: "Failed to submit.",
        RESET_CONFIRM: "Are you sure you want to reset the form?",
        DISCARD_CHANGES: "Discard changes?",
    },

    // Authentication (Client-side)
    AUTH: {
        SESSION_EXPIRED: "Your session has expired. Please login again.",
        LOGIN_REQUIRED: "Please login to continue.",
        LOGOUT_CONFIRM: "Are you sure you want to logout?",
        LOGOUT_SUCCESS: "You are successfully logged out. Thank you and have a great day.",
        UNAUTHORIZED_ACCESS: "You don't have permission to access this page.",
        REDIRECTING: "Redirecting...",
        PASSWORD_EXPIRED: "Please change your password to continue.",
        INVALID_USERNAME_PASSWORD: "Invalid username or password.",
    },

    // Search & Filter
    SEARCH: {
        NO_RESULTS: "No results found for your search.",
        SEARCH_ERROR: "Error occurred while searching.",
        CLEAR_FILTERS: "Clear all filters",
        APPLY_FILTERS: "Apply filters",
        MIN_CHARACTERS: (min) => `Please enter at least ${min} characters to search.`,
    },

    // Pagination
    PAGINATION: {
        SHOWING: (from, to, total) => `Showing ${from} to ${to} of ${total} results.`,
        NO_MORE_DATA: "No more data to load.",
        LOAD_MORE: "Load more",
        PAGE_OF: (current, total) => `Page ${current} of ${total}.`,
    },

    // Date & Time
    DATETIME: {
        INVALID_DATE_RANGE: "Invalid date range.",
        START_DATE_BEFORE_END: "Start date must be before end date.",
        SELECT_DATE: "Please select a date.",
        SELECT_TIME: "Please select a time.",
        TODAY: "Today",
        YESTERDAY: "Yesterday",
        LAST_7_DAYS: "Last 7 days",
        LAST_30_DAYS: "Last 30 days",
        CUSTOM_RANGE: "Custom range",
    },

    // Common Actions
    ACTIONS: {
        SAVE: "Save",
        CANCEL: "Cancel",
        DELETE: "Delete",
        EDIT: "Edit",
        VIEW: "View",
        ADD: "Add",
        REMOVE: "Remove",
        SUBMIT: "Submit",
        RESET: "Reset",
        CLEAR: "Clear",
        SEARCH: "Search",
        FILTER: "Filter",
        EXPORT: "Export",
        IMPORT: "Import",
        DOWNLOAD: "Download",
        UPLOAD: "Upload",
        CLOSE: "Close",
        BACK: "Back",
        NEXT: "Next",
        PREVIOUS: "Previous",
        CONFIRM: "Confirm",
        YES: "Yes",
        NO: "No",
        OK: "OK",
        APPLY: "Apply",
        REFRESH: "Refresh",
        RETRY: "Retry",
    },

    // Error Messages
    ERROR: {
        GENERIC: "Something went wrong. Please try again.",
        PAGE_NOT_FOUND: "Page not found.",
        ACCESS_DENIED: "Access denied.",
        INVALID_REQUEST: "Invalid request.",
        OPERATION_FAILED: "Operation failed.",
        TRY_AGAIN_LATER: "Please try again later.",
        CONTACT_SUPPORT: "Please contact support if the problem persists.",
        UNEXPECTED_ERROR: "An unexpected error occurred.",
    },

    // Success Messages
    SUCCESS: {
        OPERATION_COMPLETE: "Operation completed successfully.",
        CHANGES_SAVED: "Your changes have been saved.",
        ACTION_SUCCESS: (action) => `${capitalize(action)} completed successfully.`,
    },

    // Confirmation Messages
    CONFIRM: {
        DELETE_ITEM: (item) => `Are you sure you want to delete this ${item.toLowerCase()}?`,
        DELETE_MULTIPLE: (count, item) => `Are you sure you want to delete ${count} ${item.toLowerCase()}${count > 1 ? 's' : ''}?`,
        DISCARD_CHANGES: "You have unsaved changes. Do you want to discard them?",
        LEAVE_PAGE: "Are you sure you want to leave this page?",
        RESET_FORM: "Are you sure you want to reset this form?",
        CANCEL_ACTION: (action) => `Are you sure you want to cancel ${action.toLowerCase()}?`,
    },

    // Table & List
    TABLE: {
        NO_DATA: "No data available.",
        LOADING: "Loading data...",
        SELECT_ALL: "Select all",
        DESELECT_ALL: "Deselect all",
        SELECTED_ITEMS: (count) => `${count} item${count !== 1 ? 's' : ''} selected.`,
        ROWS_PER_PAGE: "Rows per page",
        SORT_BY: "Sort by",
    },

    // Modal & Dialog
    MODAL: {
        CLOSE_CONFIRM: "Are you sure you want to close?",
        UNSAVED_WARNING: "You have unsaved changes.",
    },

    // Clipboard
    CLIPBOARD: {
        COPY_SUCCESS: "Copied to clipboard.",
        COPY_FAILED: "Failed to copy.",
        PASTE_FAILED: "Failed to paste.",
    },

    // Image & Media
    MEDIA: {
        INVALID_IMAGE: "Please select a valid image file.",
        IMAGE_TOO_LARGE: (size) => `Image size must not exceed ${size}.`,
        INVALID_DIMENSIONS: (width, height) =>
            `Image dimensions must be ${width}x${height}.`,
        LOADING_IMAGE: "Loading image...",
        IMAGE_LOAD_FAILED: "Failed to load image.",
    },

    // Export & Import
    EXPORT: {
        PREPARING: "Preparing export...",
        SUCCESS: "Export completed successfully.",
        FAILED: "Export failed.",
        NO_DATA: "No data to export.",
    },

    IMPORT: {
        PROCESSING: "Processing import...",
        SUCCESS: "Import completed successfully.",
        FAILED: "Import failed.",
        INVALID_FILE: "Invalid file format.",
        PARTIAL_SUCCESS: (success, failed) =>
            `Import completed: ${success} successful, ${failed} failed.`,
    },
};

// export const STATUS_CODES = {
//     // 2XX SUCCESS
//     SUCCESS: 200,
//     CREATED: 201,
//     ACCEPTED: 202,
//     NO_CONTENT: 204,

//     // 4XX CLIENT ERROR
//     BAD_REQUEST: 400,
//     UNAUTHORIZED: 401,
//     FORBIDDEN: 403,
//     NOT_FOUND: 404,
//     NOT_ALLOWED: 405,
//     CONFLICT: 409,
//     VALIDATION_ERROR: 422,
//     TOO_MANY_REQUESTS: 429,

//     // 5XX SERVER ERROR
//     SERVER_ERROR: 500,
//     BAD_GATEWAY: 502,
//     SERVICE_UNAVAILABLE: 503,
//     GATEWAY_TIMEOUT: 504,
// };


export const MESSAGES = FRONTEND_MESSAGES;
